import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import toast from 'react-hot-toast'

// ─── Lista de instrumentos ────────────────────────────────────────────────────
const INSTRUMENTOS = [
  { base: 'Maestro',            familia: 'Maestro', tom: '-'  },
  { base: 'Flauta',             familia: 'Madeira', tom: 'C'  },
  { base: 'Flauta Piccolo',     familia: 'Madeira', tom: 'C'  },
  { base: 'Oboé',               familia: 'Madeira', tom: 'C'  },
  { base: 'Corne Inglês',       familia: 'Madeira', tom: 'F'  },
  { base: 'Clarinete',          familia: 'Madeira', tom: 'Bb' },
  { base: 'Clarinete Alto',     familia: 'Madeira', tom: 'Eb' },
  { base: 'Clarinete Baixo',    familia: 'Madeira', tom: 'Bb' },
  { base: 'Fagote',             familia: 'Madeira', tom: 'C'  },
  { base: 'Contrafagote',       familia: 'Madeira', tom: 'C'  },
  { base: 'Saxofone Soprano',   familia: 'Madeira', tom: 'Bb' },
  { base: 'Saxofone Alto',      familia: 'Madeira', tom: 'Eb' },
  { base: 'Saxofone Tenor',     familia: 'Madeira', tom: 'Bb' },
  { base: 'Saxofone Barítono',  familia: 'Madeira', tom: 'Eb' },
  { base: 'Saxofone Baixo',     familia: 'Madeira', tom: 'Bb' },
  { base: 'Trompa',             familia: 'Metal',   tom: 'F'  },
  { base: 'Trompete',           familia: 'Metal',   tom: 'Bb' },
  { base: 'Corneta',            familia: 'Metal',   tom: 'Bb' },
  { base: 'Trombone',           familia: 'Metal',   tom: 'C'  },
  { base: 'Trombone Alto',      familia: 'Metal',   tom: 'Eb' },
  { base: 'Trombone Tenor',     familia: 'Metal',   tom: 'Bb' },
  { base: 'Trombone Baixo',     familia: 'Metal',   tom: 'C'  },
  { base: 'Eufônio',            familia: 'Metal',   tom: 'C'  },
  { base: 'Bombardino',         familia: 'Metal',   tom: 'Bb' },
  { base: 'Tuba',               familia: 'Metal',   tom: 'C'  },
  { base: 'Tuba Baixo',         familia: 'Metal',   tom: 'Bb' },
  { base: 'Percussão',          familia: 'Outro',   tom: '-'  },
  { base: 'Piano',              familia: 'Outro',   tom: 'C'  },
  { base: 'Contrabaixo',        familia: 'Outro',   tom: 'C'  },
  { base: 'Violino',            familia: 'Cordas',  tom: 'C'  },
  { base: 'Viola',              familia: 'Cordas',  tom: 'C'  },
  { base: 'Violoncelo',         familia: 'Cordas',  tom: 'C'  },
]

function nomeInst(base, numero) {
  return numero > 1 ? `${base} ${numero}` : base
}
function familiaDeInst(base) {
  return INSTRUMENTOS.find(i => i.base === base)?.familia || 'Outro'
}
function tomDeInst(base) {
  return INSTRUMENTOS.find(i => i.base === base)?.tom || '—'
}

// ─── PDFViewer ────────────────────────────────────────────────────────────────
function PDFViewer({ url, pagina = 1 }) {
  const canvasRef = useRef(null)
  const renderTaskRef = useRef(null)
  const pdfDocRef = useRef(null)
  const [erro, setErro] = useState(null)
  const [carregando, setCarregando] = useState(true)

  useEffect(() => {
    if (window.pdfjsLib) return
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js'
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js'
    }
    document.head.appendChild(script)
  }, [])

  useEffect(() => {
    if (!url) return
    let cancelled = false
    async function render() {
      setCarregando(true); setErro(null)
      try {
        let tries = 0
        while (!window.pdfjsLib && tries++ < 30) await new Promise(r => setTimeout(r, 300))
        if (!window.pdfjsLib) throw new Error('PDF.js não carregou')
        if (!pdfDocRef.current || pdfDocRef.current._url !== url) {
          const doc = await window.pdfjsLib.getDocument(url).promise
          doc._url = url; pdfDocRef.current = doc
        }
        const doc = pdfDocRef.current
        const pg = Math.min(Math.max(1, pagina), doc.numPages)
        const page = await doc.getPage(pg)
        if (cancelled) return
        const canvas = canvasRef.current
        if (!canvas) return
        const dpr = window.devicePixelRatio || 1
        const viewport = page.getViewport({ scale: 1.5 * dpr })
        canvas.width = viewport.width; canvas.height = viewport.height
        canvas.style.width = `${viewport.width / dpr}px`
        canvas.style.height = `${viewport.height / dpr}px`
        if (renderTaskRef.current) renderTaskRef.current.cancel()
        renderTaskRef.current = page.render({ canvasContext: canvas.getContext('2d'), viewport })
        await renderTaskRef.current.promise
      } catch (e) {
        if (!cancelled && e?.name !== 'RenderingCancelledException') setErro('Erro ao renderizar o PDF')
      } finally {
        if (!cancelled) setCarregando(false)
      }
    }
    render()
    return () => { cancelled = true }
  }, [url, pagina])

  if (!url) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200, color: 'var(--muted)', fontSize: 14 }}>
      Sem partitura para este instrumento
    </div>
  )
  return (
    <div style={{ position: 'relative', overflow: 'auto', background: '#fff', borderRadius: 8 }}>
      {carregando && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)', zIndex: 2 }}>
          <div className="spinner" style={{ width: 32, height: 32 }} />
        </div>
      )}
      {erro && <div style={{ padding: 20, color: '#ff6b6b', textAlign: 'center' }}>{erro}</div>}
      <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%' }} />
    </div>
  )
}

// ─── SeletorInstrumento (full list) ──────────────────────────────────────────
function SeletorInstrumento({ valor, onChange }) {
  const familias = [...new Set(INSTRUMENTOS.map(i => i.familia))]
  return (
    <>
      {familias.map(fam => (
        <div key={fam} style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>{fam}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {INSTRUMENTOS.filter(i => i.familia === fam).map(inst => (
              <button key={inst.base} onClick={() => onChange(inst.base)}
                style={{
                  padding: '5px 10px', borderRadius: 20, fontSize: 12, cursor: 'pointer', border: 'none',
                  background: valor === inst.base ? 'var(--brand2)' : 'rgba(255,255,255,0.07)',
                  color: valor === inst.base ? '#fff' : 'var(--muted)',
                  fontWeight: valor === inst.base ? 700 : 400
                }}>
                {inst.base}
              </button>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

// ─── SeletorInstGrupo (instrumentos cadastrados no grupo) ─────────────────────
function SeletorInstGrupo({ instrumentosGrupo, valorBase, valorNumero, onChange }) {
  const familias = [...new Set(instrumentosGrupo.map(i => familiaDeInst(i.instrumento_base)))]
  return (
    <>
      {familias.map(fam => (
        <div key={fam} style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{fam}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {instrumentosGrupo
              .filter(i => familiaDeInst(i.instrumento_base) === fam)
              .map(inst => {
                const label = nomeInst(inst.instrumento_base, inst.numero)
                const ativo = valorBase === inst.instrumento_base && valorNumero === inst.numero
                return (
                  <button key={inst.id} onClick={() => onChange(inst.instrumento_base, inst.numero)}
                    style={{
                      padding: '8px 16px', borderRadius: 20, fontSize: 13, cursor: 'pointer', border: 'none',
                      background: ativo ? 'var(--brand2)' : 'rgba(255,255,255,0.07)',
                      color: ativo ? '#fff' : 'rgba(255,255,255,0.7)',
                      fontWeight: ativo ? 700 : 400,
                      boxShadow: ativo ? '0 2px 10px rgba(0,140,255,0.3)' : 'none'
                    }}>
                    {label}
                  </button>
                )
              })}
          </div>
        </div>
      ))}
    </>
  )
}

// ─── TelaInstrumentos (admin: gestão de instrumentos do grupo) ────────────────
const INST_PERSONALIZADO = '__personalizado__'

function TelaInstrumentos({ grupoId, onSair }) {
  const [instrumentos, setInstrumentos] = useState([])
  const [loading, setLoading] = useState(true)
  const [addBase, setAddBase] = useState('Trombone')
  const [addNumero, setAddNumero] = useState(1)
  const [addPersonalizado, setAddPersonalizado] = useState('')
  const [salvando, setSalvando] = useState(false)

  useEffect(() => { carregar() }, [])

  const nomeEfetivo = addBase === INST_PERSONALIZADO
    ? addPersonalizado.trim()
    : addBase

  async function carregar() {
    setLoading(true)
    const { data } = await supabase
      .from('classic_grupo_instrumentos')
      .select('*').eq('grupo_id', grupoId)
      .order('instrumento_base').order('numero')
    setInstrumentos(data || [])
    setLoading(false)
  }

  async function adicionar() {
    if (!nomeEfetivo) { toast.error('Digite o nome do instrumento personalizado'); return }
    if (instrumentos.some(i => i.instrumento_base === nomeEfetivo && i.numero === addNumero)) {
      toast.error(`${nomeInst(nomeEfetivo, addNumero)} já cadastrado`); return
    }
    setSalvando(true)
    try {
      const { data, error } = await supabase
        .from('classic_grupo_instrumentos')
        .insert({ grupo_id: grupoId, instrumento_base: nomeEfetivo, numero: addNumero })
        .select().single()
      if (error) throw error
      setInstrumentos(prev => [...prev, data].sort((a, b) =>
        a.instrumento_base.localeCompare(b.instrumento_base) || a.numero - b.numero))
      toast.success(`${nomeInst(nomeEfetivo, addNumero)} adicionado!`)
      if (addBase === INST_PERSONALIZADO) setAddPersonalizado('')
    } catch (e) { toast.error('Erro: ' + e.message) }
    finally { setSalvando(false) }
  }

  async function remover(inst) {
    if (!window.confirm(`Remover ${nomeInst(inst.instrumento_base, inst.numero)}?`)) return
    await supabase.from('classic_grupo_instrumentos').delete().eq('id', inst.id)
    setInstrumentos(prev => prev.filter(i => i.id !== inst.id))
    toast.success('Removido')
  }

  const byFamilia = instrumentos.reduce((acc, i) => {
    const f = familiaDeInst(i.instrumento_base)
    acc[f] = acc[f] || []
    acc[f].push(i)
    return acc
  }, {})

  const instsByFamSelect = INSTRUMENTOS.reduce((acc, i) => {
    acc[i.familia] = acc[i.familia] || []
    acc[i.familia].push(i)
    return acc
  }, {})

  return (
    <div className="app-container" style={{ background: 'var(--brand)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onSair} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>‹</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 700 }}>🎻 Instrumentos do Grupo</div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>Cadastre os instrumentos da orquestra (ex: Trombone 1, Trombone 2)</div>
        </div>
      </div>

      <div className="page-content" style={{ padding: '16px 20px', paddingBottom: 40 }}>
        {/* Formulário de adição */}
        <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 14, padding: 16, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: 'var(--gold)' }}>+ Adicionar instrumento</div>
          <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Instrumento</label>
          <select className="input" value={addBase} onChange={e => { setAddBase(e.target.value); setAddPersonalizado('') }} style={{ marginBottom: addBase === INST_PERSONALIZADO ? 8 : 12 }}>
            {Object.entries(instsByFamSelect).map(([fam, insts]) => (
              <optgroup key={fam} label={fam}>
                {insts.map(i => <option key={i.base} value={i.base}>{i.base}</option>)}
              </optgroup>
            ))}
            <optgroup label="Outro">
              <option value={INST_PERSONALIZADO}>✏️ Personalizado…</option>
            </optgroup>
          </select>
          {addBase === INST_PERSONALIZADO && (
            <input
              className="input"
              placeholder="Nome do instrumento (ex: Cítara, Bandoneón)"
              value={addPersonalizado}
              onChange={e => setAddPersonalizado(e.target.value)}
              style={{ marginBottom: 12 }}
            />
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            <div style={{ width: 100 }}>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Número</label>
              <input type="number" min={1} max={20} value={addNumero}
                onChange={e => setAddNumero(Number(e.target.value))} className="input" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Ficará como:</div>
              <div style={{ fontWeight: 700, color: 'var(--brand2)', fontSize: 16 }}>
                {nomeEfetivo ? nomeInst(nomeEfetivo, addNumero) : <span style={{ color: 'var(--muted)', fontStyle: 'italic', fontSize: 13 }}>digite o nome</span>}
              </div>
            </div>
            <button className="btn btn-primary" onClick={adicionar} disabled={salvando}
              style={{ height: 46, minWidth: 90 }}>
              {salvando ? <span className="spinner" /> : 'Adicionar'}
            </button>
          </div>
        </div>

        {/* Lista de instrumentos cadastrados */}
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" /></div>
        ) : Object.keys(byFamilia).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 14 }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🎼</div>
            Nenhum instrumento cadastrado ainda.<br />
            <span style={{ fontSize: 12 }}>Adicione os instrumentos da orquestra acima.</span>
          </div>
        ) : (
          Object.entries(byFamilia).map(([fam, insts]) => (
            <div key={fam} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{fam}</div>
              {insts.map(inst => (
                <div key={inst.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 12, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{nomeInst(inst.instrumento_base, inst.numero)}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>Tom: {tomDeInst(inst.instrumento_base)}</div>
                  </div>
                  <button onClick={() => remover(inst)}
                    style={{ background: 'none', border: 'none', color: '#ff6b6b', fontSize: 18, cursor: 'pointer', padding: 4 }}>🗑</button>
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ─── Modal: Selecionar meu instrumento no grupo ───────────────────────────────
function ModalMeuInstrumento({ grupoId, userId, instrumentosGrupo, instAtualBase, instAtualNumero, isMaestro, onSalvar, onClose }) {
  const [base, setBase] = useState(instAtualBase || (isMaestro ? 'Maestro' : ''))
  const [numero, setNumero] = useState(instAtualNumero || 1)
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (!base) { toast.error('Selecione um instrumento'); return }
    setSalvando(true)
    try {
      const { data: existe } = await supabase
        .from('classic_membro_instrumento')
        .select('id').eq('grupo_id', grupoId).eq('user_id', userId).maybeSingle()
      if (existe) {
        await supabase.from('classic_membro_instrumento')
          .update({ instrumento_base: base, numero, atualizado_em: new Date().toISOString() })
          .eq('id', existe.id)
      } else {
        await supabase.from('classic_membro_instrumento')
          .insert({ grupo_id: grupoId, user_id: userId, instrumento_base: base, numero })
      }
      toast.success(`Instrumento definido: ${nomeInst(base, numero)}`)
      onSalvar(base, numero)
    } catch (e) { toast.error('Erro: ' + e.message) }
    finally { setSalvando(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', zIndex: 200 }}>
      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', background: 'var(--card)', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 18, marginBottom: 6 }}>🎵 Meu instrumento neste grupo</div>
        <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 16 }}>
          Selecione qual instrumento você toca nesta orquestra
        </div>

        {/* Opção Maestro (apenas para admin) */}
        {isMaestro && (
          <div
            onClick={() => { setBase('Maestro'); setNumero(1) }}
            style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', marginBottom: 12, background: base === 'Maestro' ? 'rgba(255,140,0,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${base === 'Maestro' ? 'rgba(255,140,0,0.5)' : 'var(--card-border)'}`, borderRadius: 12, cursor: 'pointer' }}>
            <span style={{ fontSize: 24 }}>🎼</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--gold)' }}>Maestro</div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>Partitura de regência / score completo</div>
            </div>
            {base === 'Maestro' && <span style={{ color: 'var(--gold)', fontSize: 18 }}>✓</span>}
          </div>
        )}

        {instrumentosGrupo.length === 0 && !isMaestro ? (
          <div style={{ textAlign: 'center', padding: '30px 0', color: 'var(--muted)', fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🎼</div>
            O maestro ainda não cadastrou os instrumentos do grupo.
          </div>
        ) : instrumentosGrupo.length > 0 && (
          <>
            {isMaestro && <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8, marginTop: 4 }}>— ou selecione um instrumento —</div>}
            <SeletorInstGrupo
              instrumentosGrupo={instrumentosGrupo}
              valorBase={base}
              valorNumero={numero}
              onChange={(b, n) => { setBase(b); setNumero(n) }}
            />
          </>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button className="btn btn-outline btn-block" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary btn-block" onClick={salvar} disabled={salvando || !base}>
            {salvando ? <span className="spinner" /> : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal: Nova Música ───────────────────────────────────────────────────────
function ModalNovaPeca({ grupoId, userId, setlistId, onClose, onCriada }) {
  const [titulo, setTitulo] = useState('')
  const [compositor, setCompositor] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar() {
    if (!titulo.trim()) return toast.error('Informe o título')
    setSalvando(true)
    try {
      const { data, error } = await supabase
        .from('partituras_classicas')
        .insert({ grupo_id: grupoId, titulo: titulo.trim(), compositor: compositor.trim() || null, criado_por: userId, setlist_id: setlistId || null })
        .select().single()
      if (error) throw error
      toast.success('Música criada!')
      onCriada(data)
    } catch (e) { toast.error('Erro: ' + e.message) }
    finally { setSalvando(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', zIndex: 200 }}>
      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', background: 'var(--card)', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px' }}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 18, marginBottom: 20 }}>🎵 Nova Música</div>
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Título *</label>
        <input value={titulo} onChange={e => setTitulo(e.target.value)} placeholder="Ex: Sinfonia nº 5" className="input" style={{ marginBottom: 12 }} />
        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Compositor</label>
        <input value={compositor} onChange={e => setCompositor(e.target.value)} placeholder="Ex: Beethoven" className="input" style={{ marginBottom: 20 }} />
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline btn-block" onClick={onClose}>Cancelar</button>
          <button className="btn btn-block" onClick={salvar} disabled={salvando}>
            {salvando ? <span className="spinner" /> : 'Criar Música'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal: Nova Parte (instrumento + PDF) ─────────────────────────────────────
const TONS_VALIDOS = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B', '-']

function ModalNovaParte({ partituraId, instrumentosGrupo, onClose, onAdicionada }) {
  const useGrupo = instrumentosGrupo.length > 0
  const [instBase, setInstBase] = useState(useGrupo ? instrumentosGrupo[0].instrumento_base : 'Clarinete')
  const [numero, setNumero] = useState(useGrupo ? instrumentosGrupo[0].numero : 1)
  const [arquivo, setArquivo] = useState(null)
  const [totalPag, setTotalPag] = useState(1)
  const [enviando, setEnviando] = useState(false)
  const [modoManual, setModoManual] = useState(false)
  const [tomOverride, setTomOverride] = useState(null)

  const instInfo = INSTRUMENTOS.find(i => i.base === instBase) || INSTRUMENTOS[4]
  const tomFinal = tomOverride !== null ? tomOverride : instInfo.tom

  function handleInstChange(base, num) {
    setInstBase(base)
    if (num !== undefined) setNumero(num)
    setTomOverride(null) // reset ao trocar instrumento
  }

  async function salvar() {
    if (!arquivo) return toast.error('Selecione o PDF da partitura')
    setEnviando(true)
    try {
      const ext = arquivo.name.split('.').pop()
      const path = `classic/${partituraId}/${instBase.replace(/\s/g, '_')}_${numero}.${ext}`
      const { error: upErr } = await supabase.storage
        .from('cifras-arquivos').upload(path, arquivo, { upsert: true })
      if (upErr) throw upErr

      const { data: urlData } = supabase.storage.from('cifras-arquivos').getPublicUrl(path)
      const instrumento = nomeInst(instBase, numero)
      const { data, error } = await supabase
        .from('partitura_partes')
        .insert({ partitura_id: partituraId, instrumento_base: instBase, numero, instrumento, tom_instrumento: tomFinal, pdf_url: urlData.publicUrl, total_paginas: totalPag })
        .select().single()
      if (error) throw error
      toast.success(`${instrumento} adicionado!`)
      onAdicionada(data)
    } catch (e) { toast.error('Erro: ' + e.message) }
    finally { setEnviando(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', zIndex: 200, overflowY: 'auto' }}>
      <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', background: 'var(--card)', borderRadius: '20px 20px 0 0', padding: '24px 20px 40px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 18, marginBottom: 16 }}>Adicionar Partitura</div>

        {/* Seleção de instrumento */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <label style={{ fontSize: 12, color: 'var(--muted)' }}>Instrumento *</label>
          {useGrupo && (
            <button onClick={() => setModoManual(!modoManual)}
              style={{ background: 'none', border: 'none', color: '#00CFFF', fontSize: 11, cursor: 'pointer', padding: 0 }}>
              {modoManual ? '← Usar lista do grupo' : 'Selecionar manualmente ›'}
            </button>
          )}
        </div>

        {(useGrupo && !modoManual) ? (
          <SeletorInstGrupo
            instrumentosGrupo={instrumentosGrupo}
            valorBase={instBase}
            valorNumero={numero}
            onChange={(b, n) => handleInstChange(b, n)}
          />
        ) : (
          <>
            <SeletorInstrumento valor={instBase} onChange={v => handleInstChange(v)} />
            <div style={{ marginTop: 10, marginBottom: 4 }}>
              <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Número (1, 2…)</label>
              <input type="number" min={1} max={10} value={numero}
                onChange={e => setNumero(Number(e.target.value))} className="input" style={{ width: 80 }} />
            </div>
          </>
        )}

        <div style={{ marginTop: 14, marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label style={{ fontSize: 12, color: 'var(--muted)' }}>Tom da partitura:</label>
            <select
              value={tomFinal}
              onChange={e => setTomOverride(e.target.value)}
              className="input"
              style={{ width: 70, padding: '4px 6px', fontSize: 13, color: 'var(--gold)', fontWeight: 700 }}
            >
              {TONS_VALIDOS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            {tomOverride !== null && (
              <button onClick={() => setTomOverride(null)}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 11, cursor: 'pointer', padding: 0 }}>
                ↩ padrão ({instInfo.tom})
              </button>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand2)' }}>{nomeInst(instBase, numero)}</div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Total de páginas</label>
          <input type="number" min={1} value={totalPag}
            onChange={e => setTotalPag(Number(e.target.value))} className="input" style={{ width: 80, marginBottom: 12 }} />
        </div>

        <label style={{ fontSize: 12, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>PDF da Partitura *</label>
        <input type="file" accept=".pdf" onChange={e => setArquivo(e.target.files[0])}
          style={{ width: '100%', marginBottom: 20, color: 'var(--text)', fontSize: 13 }} />

        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline btn-block" onClick={onClose}>Cancelar</button>
          <button className="btn btn-block" onClick={salvar} disabled={enviando}>
            {enviando ? <span className="spinner" /> : 'Enviar'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Tela de Show ao Vivo (Classic) ──────────────────────────────────────────
function TelaShowAoVivo({ partituraInicial, partesIniciais, todasPecas, meuInstBase, meuInstNumero, isMaestro, grupoId, userId, onSair, onEncerrado }) {
  const [pecaAtual, setPecaAtual] = useState(partituraInicial)
  const [partes, setPartes] = useState(partesIniciais)
  const [pagina, setPagina] = useState(1)
  const [conectados, setConectados] = useState(0)
  const [selecionandoPeca, setSelecionandoPeca] = useState(false)
  const [trocandoPeca, setTrocandoPeca] = useState(false)
  const canalRef = useRef(null)

  const minhaParte = partes.find(p =>
    p.instrumento_base === meuInstBase && p.numero === (meuInstBase === 'Maestro' ? 1 : meuInstNumero)
  ) || (meuInstBase ? null : partes.find(p => p.instrumento_base && p.numero === 1))
  const totalPaginas = minhaParte?.total_paginas || 1
  const semPartitura = !minhaParte
  const meuLabel = meuInstBase === 'Maestro' ? '🎼 Maestro' : (meuInstBase ? nomeInst(meuInstBase, meuInstNumero) : 'Instrumento não definido')

  useEffect(() => {
    const canal = supabase.channel(`classic:${grupoId}:show`)
    canalRef.current = canal

    canal
      .on('presence', { event: 'sync' }, () => {
        setConectados(Object.values(canal.presenceState()).flat().length)
      })
      .on('broadcast', { event: 'virar_pagina' }, ({ payload }) => {
        if (payload?.pagina) setPagina(payload.pagina)
      })
      .on('broadcast', { event: 'trocar_peca' }, async ({ payload }) => {
        if (!payload?.pecaId) return
        setTrocandoPeca(true)
        try {
          const { data } = await supabase
            .from('partitura_partes').select('*')
            .eq('partitura_id', payload.pecaId)
          setPecaAtual({ id: payload.pecaId, titulo: payload.titulo, compositor: payload.compositor })
          setPartes(data || [])
          setPagina(1)
        } finally {
          setTrocandoPeca(false)
        }
      })
      .on('broadcast', { event: 'encerrar_classico' }, () => {
        // Membros saem automaticamente quando maestro encerra
        if (!isMaestro) {
          toast('⏹ Peça encerrada pelo maestro', { style: { background: '#071629', color: '#f0f0f0' } })
          onEncerrado?.()
        }
      })
      .subscribe(status => {
        if (status === 'SUBSCRIBED') {
          canal.track({ userId, instrumento: meuLabel, isMaestro })
          // Maestro anuncia início para todos os membros
          if (isMaestro) {
            canal.send({
              type: 'broadcast',
              event: 'iniciar_classico',
              payload: { pecaId: partituraInicial.id, titulo: partituraInicial.titulo, grupoId }
            })
          }
        }
      })

    return () => {
      canal.untrack().catch(() => {})
      supabase.removeChannel(canal)
    }
  }, [grupoId, userId, meuLabel, isMaestro])

  function encerrarPeca() {
    canalRef.current?.send({ type: 'broadcast', event: 'encerrar_classico', payload: {} })
    onSair()
  }

  function virarPagina(novaPag) {
    if (!isMaestro) return
    const pg = Math.min(Math.max(1, novaPag), totalPaginas)
    setPagina(pg)
    canalRef.current?.send({ type: 'broadcast', event: 'virar_pagina', payload: { pagina: pg } })
  }

  async function trocarPeca(peca) {
    if (!isMaestro) return
    setSelecionandoPeca(false)
    setTrocandoPeca(true)
    try {
      const { data } = await supabase.from('partitura_partes').select('*').eq('partitura_id', peca.id)
      setPecaAtual(peca)
      setPartes(data || [])
      setPagina(1)
      canalRef.current?.send({
        type: 'broadcast', event: 'trocar_peca',
        payload: { pecaId: peca.id, titulo: peca.titulo, compositor: peca.compositor }
      })
    } finally { setTrocandoPeca(false) }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#111' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(2,18,53,0.95)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <button onClick={onSair} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer' }}>‹</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e07800', display: 'inline-block', boxShadow: '0 0 6px #e07800', flexShrink: 0 }} />
            <span style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pecaAtual.titulo}</span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
            {meuLabel} · {conectados} conectado{conectados !== 1 ? 's' : ''}
            {isMaestro
              ? <span style={{ color: 'var(--gold)', marginLeft: 6 }}>🎼 Maestro</span>
              : <span style={{ marginLeft: 6 }}>· aguardando</span>}
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', background: 'rgba(255,255,255,0.06)', padding: '5px 10px', borderRadius: 8 }}>
          {pagina} / {totalPaginas}
        </div>
        {isMaestro && todasPecas.length > 1 && (
          <button onClick={() => setSelecionandoPeca(true)}
            style={{ background: 'rgba(255,140,0,0.15)', border: '1px solid rgba(255,140,0,0.4)', borderRadius: 8, color: 'var(--gold)', fontSize: 11, fontWeight: 700, padding: '6px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            🎼 Trocar
          </button>
        )}
      </div>

      {/* Partitura */}
      <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
        {trocandoPeca ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
            <div className="spinner" style={{ width: 40, height: 40 }} />
            <div style={{ color: 'var(--muted)', fontSize: 14 }}>Carregando peça...</div>
          </div>
        ) : semPartitura ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', textAlign: 'center', padding: '0 40px' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🎵</div>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Sem partitura disponível</div>
            <div style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.6 }}>
              Não há partitura para <strong style={{ color: '#00CFFF' }}>{meuLabel}</strong> na peça<br />
              <strong style={{ color: 'var(--gold)' }}>"{pecaAtual.titulo}"</strong>
            </div>
            <div style={{ marginTop: 20, fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
              Aguarde o maestro selecionar outra peça
            </div>
          </div>
        ) : (
          <PDFViewer url={minhaParte?.pdf_url} pagina={pagina} />
        )}
      </div>

      {/* Controles maestro */}
      {isMaestro ? (
        <div style={{ background: 'rgba(2,18,53,0.95)', borderTop: '1px solid rgba(255,255,255,0.08)', padding: '10px 20px 28px' }}>
          {/* Navegação de páginas */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 10 }}>
            <button onClick={() => virarPagina(pagina - 1)} disabled={pagina <= 1}
              style={{ width: 56, height: 56, borderRadius: '50%', background: pagina <= 1 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', color: pagina <= 1 ? 'var(--muted)' : '#fff', fontSize: 26, cursor: pagina <= 1 ? 'default' : 'pointer' }}>‹</button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, fontWeight: 700, fontFamily: 'Montserrat,sans-serif' }}>Pág. {pagina}</div>
              <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 2 }}>todos veem esta página</div>
            </div>
            <button onClick={() => virarPagina(pagina + 1)} disabled={pagina >= totalPaginas}
              style={{ width: 56, height: 56, borderRadius: '50%', background: pagina >= totalPaginas ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg,var(--brand2),var(--gold))', border: 'none', color: pagina >= totalPaginas ? 'var(--muted)' : '#fff', fontSize: 26, cursor: pagina >= totalPaginas ? 'default' : 'pointer', boxShadow: pagina >= totalPaginas ? 'none' : '0 4px 16px rgba(255,140,0,0.35)' }}>›</button>
          </div>
          {/* Botão Encerrar */}
          <button onClick={encerrarPeca}
            style={{ width: '100%', padding: '11px', borderRadius: 12, background: 'rgba(255,68,68,0.12)', border: '1px solid rgba(255,68,68,0.4)', color: '#ff6b6b', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'Montserrat,sans-serif' }}>
            ⏹ Encerrar Peça — avisar todos os membros
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '14px 20px 28px', background: 'rgba(2,18,53,0.95)', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#e07800', display: 'inline-block', boxShadow: '0 0 6px #e07800' }} />
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>Página <strong style={{ color: '#fff' }}>{pagina}</strong> — controlada pelo maestro</span>
        </div>
      )}

      {/* Modal: Selecionar peça (maestro) */}
      {selecionandoPeca && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'flex-end', zIndex: 100 }}>
          <div style={{ width: '100%', maxWidth: 480, margin: '0 auto', background: 'var(--card)', borderRadius: '20px 20px 0 0', padding: '20px 20px 40px', maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 17 }}>🎼 Selecionar Peça</div>
              <button onClick={() => setSelecionandoPeca(false)}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
            {todasPecas.map(peca => (
              <div key={peca.id}
                onClick={() => trocarPeca(peca)}
                style={{
                  padding: '14px 16px', background: peca.id === pecaAtual.id ? 'rgba(0,140,255,0.15)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${peca.id === pecaAtual.id ? 'rgba(0,140,255,0.4)' : 'var(--card-border)'}`,
                  borderRadius: 12, marginBottom: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12
                }}>
                <div style={{ fontSize: 24 }}>🎼</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{peca.titulo}</div>
                  {peca.compositor && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{peca.compositor}</div>}
                </div>
                {peca.id === pecaAtual.id && <span style={{ color: '#00CFFF', fontSize: 12, fontWeight: 700 }}>● Atual</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tela: Detalhes de uma partitura ──────────────────────────────────────────
function TelaPartitura({ partitura, grupoId, userId, isMaestro, instrumentosGrupo, onSair, onShowAoVivo }) {
  const [partes, setPartes] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddParte, setShowAddParte] = useState(false)

  useEffect(() => { carregarPartes() }, [partitura.id])

  async function carregarPartes() {
    setLoading(true)
    const { data } = await supabase
      .from('partitura_partes').select('*')
      .eq('partitura_id', partitura.id).order('instrumento_base').order('numero')
    setPartes(data || [])
    setLoading(false)
  }

  async function deletarParte(parteId, pdfUrl) {
    if (!window.confirm('Remover esta parte?')) return
    const path = pdfUrl?.split('/cifras-arquivos/')[1]
    if (path) await supabase.storage.from('cifras-arquivos').remove([path])
    await supabase.from('partitura_partes').delete().eq('id', parteId)
    setPartes(prev => prev.filter(p => p.id !== parteId))
    toast.success('Parte removida')
  }

  const byFamilia = partes.reduce((acc, p) => {
    const f = familiaDeInst(p.instrumento_base)
    acc[f] = acc[f] || []
    acc[f].push(p)
    return acc
  }, {})

  return (
    <div className="app-container" style={{ background: 'var(--brand)' }}>
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onSair} style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>‹</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 700 }}>{partitura.titulo}</div>
          {partitura.compositor && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{partitura.compositor}</div>}
        </div>
        <button onClick={() => onShowAoVivo(partitura, partes)}
          style={{ background: 'linear-gradient(135deg,#e07800,var(--gold))', border: 'none', borderRadius: 10, color: '#fff', fontSize: 12, fontWeight: 700, padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff', display: 'inline-block', boxShadow: '0 0 5px #fff' }} />
          Show ao Vivo
        </button>
      </div>

      <div className="page-content" style={{ padding: '16px 20px' }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>
          {partes.length} instrumento{partes.length !== 1 ? 's' : ''} cadastrado{partes.length !== 1 ? 's' : ''}
        </div>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><div className="spinner" style={{ width: 32, height: 32 }} /></div>
        ) : partes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
            <div>Nenhuma partitura adicionada</div>
            {isMaestro && <div style={{ fontSize: 13, marginTop: 6 }}>Toque + para adicionar instrumentos</div>}
          </div>
        ) : (
          Object.entries(byFamilia).map(([fam, ps]) => (
            <div key={fam} style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>{fam}</div>
              {ps.map(parte => (
                <div key={parte.id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 12, padding: '14px 16px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{parte.instrumento}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                      Tom: <span style={{ color: 'var(--gold)' }}>{parte.tom_instrumento || '—'}</span>{' · '}{parte.total_paginas} pág.
                    </div>
                  </div>
                  {parte.pdf_url && (
                    <a href={parte.pdf_url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 20, textDecoration: 'none' }} title="Ver PDF">📄</a>
                  )}
                  {isMaestro && (
                    <button onClick={() => deletarParte(parte.id, parte.pdf_url)}
                      style={{ background: 'none', border: 'none', color: '#ff6b6b', fontSize: 18, cursor: 'pointer' }}>🗑</button>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      {isMaestro && (
        <button onClick={() => setShowAddParte(true)}
          style={{ position: 'fixed', bottom: 30, right: 20, width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,var(--brand2),var(--gold))', border: 'none', fontSize: 28, color: '#fff', cursor: 'pointer', boxShadow: '0 4px 20px rgba(255,140,0,0.4)', zIndex: 10 }}>
          +
        </button>
      )}

      {showAddParte && (
        <ModalNovaParte
          partituraId={partitura.id}
          instrumentosGrupo={instrumentosGrupo}
          onClose={() => setShowAddParte(false)}
          onAdicionada={parte => { setPartes(prev => [...prev, parte]); setShowAddParte(false) }}
        />
      )}
    </div>
  )
}

// ─── ClassicPage principal ────────────────────────────────────────────────────
export default function ClassicPage() {
  const { grupoId } = useParams()
  const navigate = useNavigate()
  const { user, profile } = useAuth()
  const [searchParams] = useSearchParams()
  const setlistId = searchParams.get('setlist')
  const autoShowPecaId = searchParams.get('autoshow')

  const [pecas, setPecas] = useState([])
  const [nomeSetlist, setNomeSetlist] = useState(null)
  const [loading, setLoading] = useState(true)
  const [meuPapel, setMeuPapel] = useState('membro')
  const [view, setView] = useState('lista')
  const [pecaSelecionada, setPecaSelecionada] = useState(null)
  const [partesShow, setPartesShow] = useState([])
  const [showNovaPeca, setShowNovaPeca] = useState(false)
  const [veioPorAutoShow, setVeioPorAutoShow] = useState(false)

  // Instrumentos do grupo e instrumento do usuário neste grupo
  const [instrumentosGrupo, setInstrumentosGrupo] = useState([])
  const [meuInstBase, setMeuInstBase] = useState(null)
  const [meuInstNumero, setMeuInstNumero] = useState(1)
  const [showMeuInst, setShowMeuInst] = useState(false)

  const isAdmin = meuPapel === 'admin'
  const meuLabel = meuInstBase === 'Maestro'
    ? '🎼 Maestro'
    : (meuInstBase ? nomeInst(meuInstBase, meuInstNumero) : (profile?.instrumento || null))

  useEffect(() => { init() }, [grupoId, setlistId, autoShowPecaId])

  async function init() {
    setLoading(true)
    try {
      let pecasQuery = supabase
        .from('partituras_classicas').select('*')
        .eq('grupo_id', grupoId)
        .order('criado_em', { ascending: false })
      if (setlistId) pecasQuery = pecasQuery.eq('setlist_id', setlistId)

      const promises = [
        pecasQuery,
        supabase.from('grupo_membros').select('papel').eq('grupo_id', grupoId).eq('user_id', user.id).single(),
        setlistId ? supabase.from('setlists').select('nome').eq('id', setlistId).single() : Promise.resolve({ data: null }),
        supabase.from('classic_grupo_instrumentos').select('*').eq('grupo_id', grupoId).order('instrumento_base').order('numero'),
        supabase.from('classic_membro_instrumento').select('*').eq('grupo_id', grupoId).eq('user_id', user.id).maybeSingle(),
      ]
      // Se veio via notificação (?autoshow=pecaId), carrega as partes dessa peça de uma vez
      if (autoShowPecaId) {
        promises.push(supabase.from('partitura_partes').select('*').eq('partitura_id', autoShowPecaId))
      }

      const results = await Promise.all(promises)
      const [
        { data: pecasData },
        { data: membroData },
        { data: slData },
        { data: instGrupoData },
        { data: meuInstData },
      ] = results

      setPecas(pecasData || [])
      if (membroData) setMeuPapel(membroData.papel)
      if (slData) setNomeSetlist(slData.nome)
      setInstrumentosGrupo(instGrupoData || [])
      if (meuInstData) {
        setMeuInstBase(meuInstData.instrumento_base)
        setMeuInstNumero(meuInstData.numero)
      }

      // Auto-entrar no show se veio via notificação
      if (autoShowPecaId && pecasData) {
        const peca = pecasData.find(p => p.id === autoShowPecaId)
        const partesAutoShow = results[5]?.data || []
        if (peca) {
          setPecaSelecionada(peca)
          setPartesShow(partesAutoShow)
          setVeioPorAutoShow(true)
          setView('show')
        }
      }
    } catch (e) {
      toast.error('Erro ao carregar')
    } finally {
      setLoading(false)
    }
  }

  async function deletarPeca(pecaId) {
    if (!window.confirm('Remover esta música e todas as partituras?')) return
    await supabase.from('partituras_classicas').delete().eq('id', pecaId)
    setPecas(prev => prev.filter(p => p.id !== pecaId))
    toast.success('Música removida')
  }

  // ── Show ao Vivo ──
  if (view === 'show' && pecaSelecionada) {
    return (
      <TelaShowAoVivo
        partituraInicial={pecaSelecionada}
        partesIniciais={partesShow}
        todasPecas={pecas}
        meuInstBase={meuInstBase}
        meuInstNumero={meuInstNumero}
        isMaestro={isAdmin}
        grupoId={grupoId}
        userId={user.id}
        onSair={() => veioPorAutoShow ? setView('lista') : setView('partitura')}
        onEncerrado={() => { setVeioPorAutoShow(false); setView('lista') }}
      />
    )
  }

  // ── Gestão de instrumentos (admin) ──
  if (view === 'instrumentos') {
    return (
      <TelaInstrumentos
        grupoId={grupoId}
        onSair={() => { setView('lista'); init() }}
      />
    )
  }

  // ── Detalhes da partitura ──
  if (view === 'partitura' && pecaSelecionada) {
    return (
      <TelaPartitura
        partitura={pecaSelecionada}
        grupoId={grupoId}
        userId={user.id}
        isMaestro={isAdmin}
        instrumentosGrupo={instrumentosGrupo}
        onSair={() => setView('lista')}
        onShowAoVivo={(peca, partes) => { setPartesShow(partes); setView('show') }}
      />
    )
  }

  // ── Lista de peças ──
  return (
    <div className="app-container" style={{ background: 'var(--brand)' }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => setlistId ? navigate(`/grupo/${grupoId}/setlists`) : navigate(`/grupo/${grupoId}`)}
          style={{ background: 'none', border: 'none', color: 'var(--muted)', fontSize: 20, cursor: 'pointer' }}>‹</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Montserrat,sans-serif', fontSize: 18, fontWeight: 700 }}>
            🎼 {nomeSetlist || 'Classic'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            {meuLabel
              ? `${meuLabel}${isAdmin ? ' · 👑 Maestro' : ''}`
              : 'Instrumento não definido'}
          </div>
        </div>
        {/* Botão de gestão de instrumentos (admin) */}
        {isAdmin && (
          <button onClick={() => setView('instrumentos')}
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: 'var(--gold)', fontWeight: 600 }}
            title="Gerenciar instrumentos">
            🎻
          </button>
        )}
      </div>

      {/* Card: Meu instrumento neste grupo */}
      <div style={{ margin: '14px 20px 0' }}>
        <div
          onClick={() => setShowMeuInst(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: meuInstBase ? 'rgba(0,140,255,0.08)' : 'rgba(255,140,0,0.08)', border: `1px solid ${meuInstBase ? 'rgba(0,140,255,0.25)' : 'rgba(255,140,0,0.3)'}`, borderRadius: 12, cursor: 'pointer' }}>
          <div style={{ fontSize: 22 }}>{meuInstBase ? '🎵' : '⚠️'}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>Meu instrumento neste grupo</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: meuInstBase ? '#00CFFF' : 'var(--gold)' }}>
              {meuInstBase ? nomeInst(meuInstBase, meuInstNumero) : 'Não definido — toque para selecionar'}
            </div>
          </div>
          <div style={{ color: 'var(--muted)', fontSize: 16 }}>✏️</div>
        </div>
      </div>

      {/* Lista de peças */}
      <div className="page-content" style={{ padding: '16px 20px', paddingBottom: 90 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div className="spinner" style={{ width: 32, height: 32 }} />
          </div>
        ) : pecas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎼</div>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Nenhuma música cadastrada</div>
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>
              {isAdmin ? 'Toque + para adicionar a primeira música.' : 'Aguarde o maestro adicionar músicas.'}
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pecas.map(peca => (
              <div key={peca.id}
                onClick={() => { setPecaSelecionada(peca); setView('partitura') }}
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--card-border)', borderRadius: 14, padding: '16px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 32 }}>🎼</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{peca.titulo}</div>
                  {peca.compositor && <div style={{ fontSize: 12, color: 'var(--muted)' }}>{peca.compositor}</div>}
                </div>
                {isAdmin && (
                  <button onClick={e => { e.stopPropagation(); deletarPeca(peca.id) }}
                    style={{ background: 'none', border: 'none', color: '#ff6b6b', fontSize: 18, cursor: 'pointer', padding: 4 }}>🗑</button>
                )}
                <span style={{ color: 'var(--muted)', fontSize: 20 }}>›</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bottom Nav */}
      <div style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, background: 'rgba(2,18,53,0.97)', backdropFilter: 'blur(12px)', borderTop: '1px solid var(--card-border)', display: 'flex', padding: '10px 0 24px', zIndex: 50 }}>
        {[['🏠', 'Feed'], ['👥', 'Membros'], null, ['📅', 'Setlists'], ['🎼', 'Classic']].map((item, i) => {
          if (!item) return (
            <div key="add" style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              {isAdmin ? (
                <button onClick={() => setShowNovaPeca(true)} style={{ width: 52, height: 52, borderRadius: '50%', marginTop: -26, background: 'linear-gradient(135deg, var(--brand2), var(--gold))', border: 'none', fontSize: 26, color: '#fff', cursor: 'pointer', boxShadow: '0 4px 20px rgba(255,140,0,0.4)' }}>+</button>
              ) : <div style={{ width: 52, height: 52, marginTop: -26 }} />}
            </div>
          )
          const ativo = item[1] === 'Classic'
          return (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, cursor: 'pointer', color: ativo ? 'var(--brand2)' : 'var(--muted)' }}
              onClick={() => {
                if (item[1] === 'Feed') navigate(`/grupo/${grupoId}`)
                else if (item[1] === 'Setlists') navigate(`/grupo/${grupoId}/setlists`)
                else if (item[1] === 'Membros') navigate(`/grupo/${grupoId}/membros`)
              }}>
              <span style={{ fontSize: 20 }}>{item[0]}</span>
              <span style={{ fontSize: 10 }}>{item[1]}</span>
            </div>
          )
        })}
      </div>

      {/* Modal nova peça */}
      {showNovaPeca && (
        <ModalNovaPeca
          grupoId={grupoId} userId={user.id} setlistId={setlistId}
          onClose={() => setShowNovaPeca(false)}
          onCriada={peca => { setPecas(prev => [peca, ...prev]); setShowNovaPeca(false) }}
        />
      )}

      {/* Modal meu instrumento */}
      {showMeuInst && (
        <ModalMeuInstrumento
          grupoId={grupoId} userId={user.id}
          instrumentosGrupo={instrumentosGrupo}
          instAtualBase={meuInstBase} instAtualNumero={meuInstNumero}
          isMaestro={isAdmin}
          onSalvar={(b, n) => { setMeuInstBase(b); setMeuInstNumero(n); setShowMeuInst(false) }}
          onClose={() => setShowMeuInst(false)}
        />
      )}
    </div>
  )
}
